/**
 * Typed client for the **V2 audit service** (`/audit/v1`) — the one read route
 * the Edit History drawer renders: an entity's change history, newest first.
 *
 * Contract: `optimalhr-v2-platform` → `packages/contracts/audit/openapi.yaml`
 * (`getEntityHistory`). The tenant comes from the access token, never the path.
 *
 * Wire shapes are PascalCase; everything is normalised to camelCase ONCE at
 * this boundary (CLAUDE.md "Wire-shape boundary"). Unknown enum values are
 * surfaced as `'Unknown'` and reported with `warnOnce` (field name + raw enum
 * value only — never an attribute value, which may be personal data).
 *
 * The audit service is V2-only — there is no legacy backend behind it. It is
 * routed to the V2 host only in an env that names `audit` in
 * `VITE_API_V2_SERVICES` (development today); {@link isAuditAvailable} lets a
 * screen hide the Edit History affordance everywhere else, so production never
 * sends an `/audit/…` request to the legacy host.
 */
import { isAxiosError } from 'axios';

import { httpClient } from '@/api/httpClient';
import { isV2Service, readApiHostEnv, type ApiHostEnv } from '@/api/apiHosts';
import { warnOnce } from '@/api/warnOnce';
// Types only: the iamUsers normaliser is loaded on demand in
// `listHistoryActors`, so it never lands in the shared `ui` chunk.
import type { CognitoUserWire, IamUser } from '@/api/services/iamUsers';

/** The page size the contract defaults to. */
export const DEFAULT_HISTORY_LIMIT = 50;
/** The largest page the contract serves. */
export const MAX_HISTORY_LIMIT = 100;

// ---------------------------------------------------------------------------
// Wire shapes (PascalCase — as the V2 audit contract ships them)
// ---------------------------------------------------------------------------

/** One audit line as the API returns it. Every field is optional defensively. */
export interface HistoryEntryWire {
  At?: string;
  Action?: string;
  Actor?: string;
  RequestId?: string;
  Source?: string;
  Entity?: string;
  EntityKey?: string;
  ChangedAttributes?: string[];
  Before?: Record<string, unknown> | null;
  After?: Record<string, unknown> | null;
  Truncated?: boolean;
  /** Not in the contract yet, but the writer records it alongside `Truncated`. */
  TruncatedAttributes?: string[];
}

/** One page as the API returns it. */
export interface HistoryPageWire {
  Items?: HistoryEntryWire[];
  NextCursor?: string;
}

// ---------------------------------------------------------------------------
// FE-facing shapes (camelCase)
// ---------------------------------------------------------------------------

export type HistoryAction = 'Create' | 'Update' | 'Delete' | 'Unknown';
export type HistorySource = 'api' | 'job' | 'migration' | 'Unknown';

/** One audit line, normalised. */
export interface HistoryEntry {
  /** ISO-8601 instant of the change ('' when the server sent none). */
  at: string;
  action: HistoryAction;
  /** Who made the change, as the server records it. */
  actor: string;
  requestId: string;
  source: HistorySource;
  entity: string;
  entityKey: string;
  /** Every attribute the write changed. */
  changedAttributes: string[];
  /** Changed attributes as they were (empty on a create). */
  before: Record<string, unknown>;
  /** Changed attributes as they are now (empty on a delete). */
  after: Record<string, unknown>;
  /** True when at least one value was too large to keep. */
  truncated: boolean;
  /** The attributes whose values were dropped (empty when unknown). */
  truncatedAttributes: string[];
}

/** One page of history, normalised. */
export interface HistoryPage {
  items: HistoryEntry[];
  /** Cursor for the next page; `undefined` on the last page. */
  nextCursor: string | undefined;
}

/** Paging options for {@link getEntityHistory}. */
export interface HistoryQuery {
  cursor?: string;
  limit?: number;
}

const ACTIONS: Record<string, HistoryAction> = {
  Create: 'Create',
  Update: 'Update',
  Delete: 'Delete',
};

const SOURCES: Record<string, HistorySource> = {
  api: 'api',
  job: 'job',
  migration: 'migration',
};

/**
 * Map a wire `Action` to the union, surfacing unknown values.
 * @param raw The raw wire value.
 * @returns The action, or `'Unknown'`.
 */
export function normalizeAction(raw: unknown): HistoryAction {
  if (typeof raw === 'string' && Object.hasOwn(ACTIONS, raw)) return ACTIONS[raw]!;
  warnOnce(`Unknown audit Action: ${String(raw)}`);
  return 'Unknown';
}

/**
 * Map a wire `Source` to the union, surfacing unknown values.
 * @param raw The raw wire value.
 * @returns The source, or `'Unknown'`.
 */
export function normalizeSource(raw: unknown): HistorySource {
  if (typeof raw === 'string' && Object.hasOwn(SOURCES, raw)) return SOURCES[raw]!;
  warnOnce(`Unknown audit Source: ${String(raw)}`);
  return 'Unknown';
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function record(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/**
 * Normalise one wire audit line.
 * @param w The wire entry.
 * @returns The FE-facing entry.
 */
export function normalizeHistoryEntry(w: HistoryEntryWire): HistoryEntry {
  return {
    at: str(w.At),
    action: normalizeAction(w.Action),
    actor: str(w.Actor),
    requestId: str(w.RequestId),
    source: normalizeSource(w.Source),
    entity: str(w.Entity),
    entityKey: str(w.EntityKey),
    changedAttributes: strList(w.ChangedAttributes),
    before: record(w.Before),
    after: record(w.After),
    truncated: w.Truncated === true,
    truncatedAttributes: strList(w.TruncatedAttributes),
  };
}

/**
 * Normalise one wire page. A missing / non-array `Items` is an empty page; an
 * empty-string `NextCursor` is treated as "last page".
 * @param w The wire page (may be null/undefined on a malformed response).
 * @returns The FE-facing page.
 */
export function normalizeHistoryPage(w: HistoryPageWire | null | undefined): HistoryPage {
  const items = Array.isArray(w?.Items) ? w.Items : [];
  const next = w?.NextCursor;
  return {
    items: items.map(normalizeHistoryEntry),
    nextCursor: typeof next === 'string' && next.length > 0 ? next : undefined,
  };
}

/**
 * Whether the audit service is reachable in this env (on the V2 host).
 * @param env Host env override, for tests.
 * @returns `true` when `audit` is switched on and the V2 host is configured.
 */
export function isAuditAvailable(env: ApiHostEnv = readApiHostEnv()): boolean {
  return isV2Service('audit', env);
}

/**
 * Read one page of an entity's change history, newest first.
 *
 * Both path params are percent-encoded as single segments: `entityKey` is the
 * audited item's own sort key and carries `#` (`ORGUNIT#emp-1#unit-7` →
 * `ORGUNIT%23emp-1%23unit-7`), which would otherwise start a URL fragment.
 * Encoded exactly once: callers pass the raw key, and axios does not re-encode
 * the path. The call opts out of the global error dialog (`skipGlobalError`);
 * the caller renders the failure.
 * @param employerId The employer the entity belongs to.
 * @param entityKey The audited item's sort key (raw, not pre-encoded).
 * @param query Optional `cursor` / `limit` (clamped to 1–100).
 * @returns One normalised page.
 * @throws AxiosError on a non-2xx response or network failure.
 */
export async function getEntityHistory(
  employerId: string,
  entityKey: string,
  query: HistoryQuery = {},
): Promise<HistoryPage> {
  const params: Record<string, string | number> = {};
  if (query.cursor) params.cursor = query.cursor;
  if (query.limit !== undefined && Number.isFinite(query.limit)) {
    params.limit = Math.min(MAX_HISTORY_LIMIT, Math.max(1, Math.trunc(query.limit)));
  }
  const { data } = await httpClient.get<HistoryPageWire>(
    `/audit/v1/employers/${encodeURIComponent(employerId)}/entities/${encodeURIComponent(entityKey)}/history`,
    // The Edit History drawer shows its own inline error + retry, so a failed
    // read must not also open the global error dialog on top of it.
    { params, skipGlobalError: true },
  );
  return normalizeHistoryPage(data);
}

// ---------------------------------------------------------------------------
// Errors + retry policy
// ---------------------------------------------------------------------------

/**
 * The HTTP status of a failed request, or `undefined` for a network failure /
 * a non-axios error.
 * @param err The thrown value.
 * @returns The status code, if any.
 */
function statusOf(err: unknown): number | undefined {
  return isAxiosError(err) ? err.response?.status : undefined;
}

/**
 * Whether a history read failed because the caller may not see it (403).
 * @param err The thrown value.
 * @returns `true` on a 403.
 */
export function isHistoryForbidden(err: unknown): boolean {
  return statusOf(err) === 403;
}

/**
 * TanStack `retry` predicate for the audit + actor reads: retry ONCE, and only
 * a transient failure — a network error (axios error with no response) or a
 * 5xx. A 4xx (403 / 404 / 422 …) is final: retrying it only delays the
 * message the user needs to see.
 * @param failureCount Failures so far (TanStack passes 0 after the first).
 * @param err The thrown value.
 * @returns `true` to retry.
 */
export function shouldRetryHistoryRead(failureCount: number, err: unknown): boolean {
  if (failureCount >= 1) return false;
  if (!isAxiosError(err)) return false;
  const status = err.response?.status;
  return status === undefined || status >= 500;
}

// ---------------------------------------------------------------------------
// Actors ("Who") — resolved from the V2 identity user list
// ---------------------------------------------------------------------------

/** How a history actor is shown. */
export interface HistoryActor {
  /** Display name ('' when the user has none). */
  name: string;
  /** Email ('' when unknown). */
  email: string;
}

/**
 * The identity user list the Edit History drawer resolves actors against —
 * the same `GET /identity/v1/employers/{e}/users` (and the same normalised
 * `IamUser[]`) Users & Roles reads, so the two share one TanStack cache entry.
 * Unlike Users & Roles it opts out of the global error dialog: a user who may
 * not list users still sees the history, just with short ids for "Who".
 * @param employerId The employer.
 * @returns The users, normalised.
 * @throws AxiosError on a non-2xx response or network failure.
 */
export async function listHistoryActors(employerId: string): Promise<IamUser[]> {
  const { data } = await httpClient.get<CognitoUserWire[] | { Users?: CognitoUserWire[] }>(
    `/identity/v1/employers/${encodeURIComponent(employerId)}/users`,
    { skipGlobalError: true },
  );
  const wireList = Array.isArray(data) ? data : (data?.Users ?? []);
  // Same normaliser Users & Roles uses → the shared cache entry has one shape.
  const { normalizeUser, resolveLinkedEmployeeEnv } = await import('@/api/services/iamUsers');
  const env = resolveLinkedEmployeeEnv();
  return wireList.map((u) => normalizeUser(u, env));
}

/**
 * Whether the identity service (the actor lookup) is reachable in this env.
 * @param env Host env override, for tests.
 * @returns `true` when `identity` is on the V2 host.
 */
export function isActorLookupAvailable(env: ApiHostEnv = readApiHostEnv()): boolean {
  return isV2Service('identity', env);
}

/**
 * Index users by every id an audit line may record for them: the Cognito
 * `sub` attribute and the username (equal in some pools, not in others).
 * @param users The identity users.
 * @returns id → actor.
 */
export function buildActorDirectory(
  users: readonly IamUser[] | null | undefined,
): Map<string, HistoryActor> {
  const dir = new Map<string, HistoryActor>();
  for (const u of users ?? []) {
    const name =
      u.employeeName || [u.givenName, u.familyName].filter((s) => s && s.trim()).join(' ');
    const actor: HistoryActor = { name: name.trim(), email: u.email };
    const sub = (u.attributes ?? []).find((a) => a?.Name === 'sub')?.Value;
    if (sub) dir.set(sub, actor);
    if (u.username) dir.set(u.username, actor);
  }
  return dir;
}

const UUID_RE = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/**
 * Whether an actor string looks like an opaque user id (a Cognito `sub`)
 * rather than something already readable (an email, `system`).
 * @param actor The raw actor.
 * @returns `true` for a UUID-shaped id.
 */
export function isOpaqueUserId(actor: string): boolean {
  return UUID_RE.test(actor);
}

/**
 * A short, still-distinguishable form of an opaque id: its first 8 characters.
 * @param id The id.
 * @returns e.g. `190ae5ac…`.
 */
export function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}
